import {
  FolderKanban,
  BarChart3,
  Shield,
  Users,
  LogIn,
  LayoutGrid,
  Briefcase,
  Smartphone,
  Apple,
} from "lucide-react";
import { useNavigate } from "react-router";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, token, isMobileApp } = useAuth();

  useEffect(() => {
    if (isMobileApp && user && token) {
      navigate("/home");
    }
  }, [isMobileApp, user, token, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header with Login button */}
      <header className="absolute inset-x-0 top-0 z-20">
        <div className="container mx-auto flex items-center justify-between px-6 py-5">
          <div className="flex items-center">
            <img
              src="/logo.png"
              alt="Sarah"
              className="h-10 w-auto object-contain"
            />
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <div className="hidden h-6 w-px bg-gray-200 dark:bg-gray-700 sm:block" />
            <button
              onClick={() => navigate("/signin")}
              className="flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 transition-all hover:bg-indigo-700 hover:shadow-indigo-500/30 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              <LogIn className="h-4 w-4" />
              <span>Iniciar Sesion</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="container mx-auto px-6 pt-32 pb-16">
        <div className="flex flex-col items-center text-center lg:flex-row lg:text-left">
          <div className="mb-12 lg:mb-0 lg:w-1/2 lg:pr-12">
            <h1 className="mb-6 text-4xl font-bold text-gray-800 dark:text-white lg:text-6xl">
              Gestion Multiproyecto
              <span className="text-indigo-600 dark:text-indigo-400">
                {" "}
                Empresarial
              </span>
            </h1>
            <p className="mb-8 text-lg text-gray-600 dark:text-gray-300 lg:text-xl">
              Administra multiples proyectos de diversas empresas desde una sola
              plataforma. Control total de tareas, recursos y equipos en tiempo
              real.
            </p>
          </div>
          <div className="lg:w-1/2">
            <div className="relative">
              <div className="absolute -inset-4 rounded-lg bg-gradient-to-r from-indigo-400 to-purple-400 opacity-20 blur-lg"></div>
              <div className="relative rounded-2xl bg-white/50 p-8 backdrop-blur-sm dark:bg-gray-800/50">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="rounded-lg bg-white p-4 shadow-lg dark:bg-gray-700">
                      <FolderKanban className="mb-2 h-8 w-8 text-indigo-600" />
                      <h3 className="font-semibold">Proyecto A</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        En desarrollo
                      </p>
                    </div>
                    <div className="rounded-lg bg-white p-4 shadow-lg dark:bg-gray-700">
                      <BarChart3 className="mb-2 h-8 w-8 text-green-600" />
                      <h3 className="font-semibold">Progreso</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        85% completado
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4 pt-8">
                    <div className="rounded-lg bg-white p-4 shadow-lg dark:bg-gray-700">
                      <Briefcase className="mb-2 h-8 w-8 text-purple-600" />
                      <h3 className="font-semibold">Empresas</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Multi-cliente
                      </p>
                    </div>
                    <div className="rounded-lg bg-white p-4 shadow-lg dark:bg-gray-700">
                      <Users className="mb-2 h-8 w-8 text-orange-600" />
                      <h3 className="font-semibold">Equipos</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Colaboracion
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-16 dark:bg-gray-900">
        <div className="container mx-auto px-6">
          <h2 className="mb-12 text-center text-3xl font-bold text-gray-800 dark:text-white">
            Caracteristicas Principales
          </h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-lg bg-gray-50 p-6 dark:bg-gray-800">
              <LayoutGrid className="mb-4 h-12 w-12 text-indigo-600" />
              <h3 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white">
                Gestion Multi-Proyecto
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Organiza y supervisa multiples proyectos de diferentes clientes
                o empresas simultaneamente.
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-6 dark:bg-gray-800">
              <BarChart3 className="mb-4 h-12 w-12 text-green-600" />
              <h3 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white">
                Seguimiento en Tiempo Real
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Visualiza el avance de cada proyecto con metricas detalladas y
                reportes automatizados.
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-6 dark:bg-gray-800">
              <Shield className="mb-4 h-12 w-12 text-purple-600" />
              <h3 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white">
                Seguridad de Datos
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Proteccion de nivel empresarial para la informacion sensible de
                cada uno de tus proyectos.
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-6 dark:bg-gray-800">
              <Users className="mb-4 h-12 w-12 text-orange-600" />
              <h3 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white">
                Colaboracion Eficiente
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Asigna roles y permisos especificos para que tus equipos
                trabajen en armonia.
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-6 dark:bg-gray-800">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
                <FolderKanban className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white">
                Organizacion Inteligente
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Centraliza toda la documentacion y archivos relevantes de cada
                proyecto en un solo lugar.
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-6 dark:bg-gray-800">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900">
                <Briefcase className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white">
                Escalabilidad Total
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Añade nuevas empresas y proyectos sin limites a medida que tu
                negocio crece.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile App Download Section */}
      <div className="bg-gray-50 py-16 dark:bg-gray-800/50">
        <div className="container mx-auto px-6 text-center">
          <h2 className="mb-8 text-3xl font-bold text-gray-800 dark:text-white">
            Descarga nuestra App Movil
          </h2>
          <div className="flex flex-col items-center justify-center gap-6 sm:flex-row">
            {/* Android Download */}
            <div className="flex flex-col items-center gap-2">
              <a
                href="/android.apk"
                target="_blank"
                rel="noopener noreferrer"
                download="android.apk"
                className="flex items-center gap-3 rounded-xl bg-gray-900 px-8 py-4 text-white transition hover:bg-gray-800 dark:bg-indigo-600 dark:hover:bg-indigo-700"
              >
                <Smartphone className="h-8 w-8" />
                <div className="text-left">
                  <p className="text-xs uppercase">Descargar para</p>
                  <p className="text-xl font-bold">Android (APK)</p>
                </div>
              </a>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">
                * Si aparece un aviso de seguridad, selecciona "Conservar" o
                "Descargar de todos modos".
              </p>
            </div>

            {/* Mac/iOS Download (Coming Soon) */}
            <div className="flex items-center gap-3 rounded-xl border-2 border-dashed border-gray-300 px-8 py-4 text-gray-400 dark:border-gray-600 dark:text-gray-500">
              <Apple className="h-8 w-8" />
              <div className="text-left">
                <p className="text-xs uppercase">Disponible para</p>
                <p className="text-xl font-bold">iOS</p>
                <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-500">
                  Proximamente
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="mb-4 text-3xl font-bold text-white">
            ¿Listo para llevar tus proyectos al siguiente nivel?
          </h2>
          <p className="mb-8 text-lg text-indigo-100">
            unete a los profesionales que ya optimizan su gestion multi-proyecto
            de forma inteligente.
          </p>
        </div>
      </div>
    </div>
  );
}
